import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { authService } from "@catalog/supabase";

interface User {
  id: string;
  email: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.log("⚠️ Loading timeout reached - forcing loading to false");
      setIsLoading(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, []);

  // Session refresh function
  const refreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await authService.refreshSession();

      if (result.session?.user) {
        setUser({
          id: result.session.user.id,
          email: result.session.user.email || "",
          name:
            result.session.user.user_metadata?.full_name ||
            result.session.user.email?.split("@")[0],
        });
      }
    } catch (error) {
      console.error("Error refreshing session:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log("🔄 Getting initial session...");
        const session = await authService.getCurrentSession();
        if (session?.user) {
          const userData = {
            id: session.user.id,
            email: session.user.email || "",
            name:
              session.user.user_metadata?.full_name ||
              session.user.email?.split("@")[0],
          };
          setUser(userData);
          console.log(
            "✅ Initial session - User authenticated:",
            userData.email
          );

          // Don't manually log here - the auth state change handler will do it
        } else {
          setUser(null);
          console.log("❌ Initial session - No session found");
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
        setUser(null);
      } finally {
        console.log("🔄 Setting loading to false");
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = authService.onAuthStateChange(async (event, session) => {
      // Only log significant auth state changes to reduce noise
      if (["SIGNED_IN", "SIGNED_OUT", "INITIAL_SESSION"].includes(event)) {
        console.log(
          `🔄 Auth state change: ${event}`,
          session?.user?.email || "no user"
        );
      }

      if (event === "SIGNED_IN" && session?.user) {
        try {
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            name:
              session.user.user_metadata?.full_name ||
              session.user.email?.split("@")[0],
          });
          setIsLoading(false); // Ensure loading is stopped
        } catch (error) {
          console.error("Error handling auth:", error);
          setUser(null);
          setIsLoading(false);
        }
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setIsLoading(false);
      } else if (event === "TOKEN_REFRESHED") {
        // Don't change loading state for token refresh
        console.log("🔄 Token refreshed silently");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
